import { Component } from '@theme/component';

/**
 * Static EU size conversion table.
 * Source: Audley Shoes size chart (EU / US-CA / UK / JA / AU).
 */
const SIZE_TABLE = [
  { eu: 35, us: 4.5, uk: 2, ja: 20.5, au: 3 },
  { eu: 35.5, us: 5, uk: 2.5, ja: 21, au: 3.5 },
  { eu: 36, us: 5.5, uk: 3, ja: 21.5, au: 4 },
  { eu: 36.5, us: 6, uk: 3.5, ja: 22, au: 4.5 },
  { eu: 37, us: 6.5, uk: 4, ja: 22.5, au: 5 },
  { eu: 37.5, us: 7, uk: 4.5, ja: 23, au: 5.5 },
  { eu: 38, us: 7.5, uk: 5, ja: 23.5, au: 6 },
  { eu: 38.5, us: 8, uk: 5.5, ja: 24, au: 6.5 },
  { eu: 39, us: 8.5, uk: 6, ja: 24.5, au: 7 },
  { eu: 39.5, us: 9, uk: 6.5, ja: 25, au: 7.5 },
  { eu: 40, us: 9.5, uk: 7, ja: 25.5, au: 8 },
  { eu: 40.5, us: 10, uk: 7.5, ja: 26, au: 8.5 },
  { eu: 41, us: 10.5, uk: 8, ja: 26.5, au: 9 },
  { eu: 42, us: 11, uk: 8.5, ja: 27, au: 9.5 },
];

export const SIZE_SYSTEMS = ['EU', 'US', 'UK', 'JA', 'AU'];

const STORAGE_SYSTEM_KEY = 'audley:size-system';
const STORAGE_OVERRIDE_KEY = 'audley:size-system-override';
const CHANGE_EVENT = 'size-system:change';

/** @param {string} raw */
function parseEuValue(raw) {
  const match = String(raw).replace(',', '.').match(/\d+(\.\d+)?/);
  return match ? parseFloat(match[0]) : null;
}

/**
 * @param {string} euValue
 * @param {string} system
 */
export function convertSize(euValue, system) {
  const key = system.toLowerCase();
  const target = parseEuValue(euValue);
  if (target === null || key === 'eu') return euValue;

  const row = SIZE_TABLE.find((entry) => Math.abs(entry.eu - target) < 0.01);
  if (!row || !(key in row)) return euValue;

  return String(row[/** @type {'us' | 'uk' | 'ja' | 'au'} */ (key)]);
}

function getMarketDefault() {
  const system = document.documentElement.dataset.defaultSizeSystem;
  return system && SIZE_SYSTEMS.includes(system) ? system : 'EU';
}

function readStorage(key) {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStorage(key, value) {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Storage unavailable (private browsing, etc). Preference just won't persist.
  }
}

export function getCurrentSystem() {
  const hasOverride = readStorage(STORAGE_OVERRIDE_KEY) === 'true';
  const stored = readStorage(STORAGE_SYSTEM_KEY);

  if (hasOverride && stored && SIZE_SYSTEMS.includes(stored)) return stored;

  return getMarketDefault();
}

function applySystem(system) {
  document.documentElement.dataset.sizeSystem = system;

  for (const el of document.querySelectorAll('[data-size-value]')) {
    if (!(el instanceof HTMLElement)) continue;
    if (!el.dataset.sizeOriginal) el.dataset.sizeOriginal = el.textContent?.trim() ?? '';
    el.textContent = convertSize(el.dataset.sizeOriginal, system);
  }
}

/**
 * @param {string} system
 * @param {{ manual?: boolean }} [options]
 */
export function setSizeSystem(system, { manual = false } = {}) {
  if (!SIZE_SYSTEMS.includes(system)) return;

  writeStorage(STORAGE_SYSTEM_KEY, system);
  if (manual) writeStorage(STORAGE_OVERRIDE_KEY, 'true');

  applySystem(system);
  document.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { system } }));
}

const OBSERVER_OPTIONS = { childList: true, subtree: true, characterData: true };

function init() {
  applySystem(getCurrentSystem());

  const observer = new MutationObserver((mutations) => {
    const shouldReapply = mutations.some((mutation) => {
      if (mutation.type === 'characterData') {
        return Boolean(mutation.target.parentElement?.closest('[data-size-value]'));
      }

      return [...mutation.addedNodes].some(
        (node) => node instanceof Element && (node.matches('[data-size-value]') || node.querySelector('[data-size-value]'))
      );
    });

    if (!shouldReapply) return;

    // Applying the conversion mutates text nodes ourselves, which would otherwise
    // re-trigger this same observer. Disconnect while we write, then resume.
    observer.disconnect();
    applySystem(getCurrentSystem());
    observer.observe(document.body, OBSERVER_OPTIONS);
  });

  observer.observe(document.body, OBSERVER_OPTIONS);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}

export class SizeSystemToggle extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.syncActiveState(getCurrentSystem());
    document.addEventListener(CHANGE_EVENT, this.handleChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CHANGE_EVENT, this.handleChange);
  }

  handleChange = (/** @type {CustomEvent<{ system: string }>} */ event) => {
    this.syncActiveState(event.detail.system);
  };

  /** @param {{ system: string }} data */
  setSystem = (data) => {
    setSizeSystem(data.system, { manual: true });
  };

  /** @param {string} system */
  syncActiveState(system) {
    for (const button of this.querySelectorAll('[data-size-system-option]')) {
      const isActive = button.getAttribute('data-size-system-option') === system;
      button.setAttribute('aria-pressed', String(isActive));
    }
  }
}

if (!customElements.get('size-system-toggle')) {
  customElements.define('size-system-toggle', SizeSystemToggle);
}
